/**
 * Headshot kill indicator.
 *
 * On headshot kill: displays a skull icon / "HEADSHOT" text flash that scales
 * up from 0 → 1.2 → 1.0 over 0.3 s, then fades out over 1.0 s.
 * Total animation duration: 1.3 s.
 */

const SCALE_UP_DURATION = 0.15; // 0 → 1.2
const SCALE_DOWN_DURATION = 0.15; // 1.2 → 1.0
const SCALE_TOTAL = SCALE_UP_DURATION + SCALE_DOWN_DURATION; // 0.3 s
const FADE_DURATION = 1.0; // after scale settles
const TOTAL_DURATION = SCALE_TOTAL + FADE_DURATION; // 1.3 s

const OVERSHOOT_SCALE = 1.2;
const SETTLE_SCALE = 1.0;
const CRITICAL_OVERSHOOT_SCALE = 1.5;
const CRITICAL_SETTLE_SCALE = 1.16;

export class HeadshotIndicator {
  private container: HTMLElement;
  private el: HTMLElement;
  private _isActive = false;
  private _age = 0;
  private _scale = 0;
  private _opacity = 0;
  private _critical = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.style.cssText = [
      'position: fixed',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'pointer-events: none',
      'z-index: 32',
      'display: flex',
      'align-items: center',
      'justify-content: center',
    ].join('; ');

    this.el = document.createElement('div');
    this.el.style.cssText = [
      'position: fixed',
      'top: 25%',
      'left: 50%',
      'font-family: "Courier New", monospace',
      'font-size: 28px',
      'font-weight: bold',
      'color: #ff4444',
      'text-shadow: 0 0 8px rgba(255,68,68,0.8), 2px 2px 4px #000',
      'letter-spacing: 3px',
      'text-transform: uppercase',
      'opacity: 0',
      'transform: translateX(-50%) scale(0)',
      'pointer-events: none',
    ].join('; ');
    this.el.textContent = '\u2620 HEADSHOT'; // skull and crossbones + text

    this.container.appendChild(this.el);
    document.body.appendChild(this.container);
  }

  // ── Public API ──

  get isActive(): boolean {
    return this._isActive;
  }

  get currentScale(): number {
    return this._scale;
  }

  get currentOpacity(): number {
    return this._opacity;
  }

  /** Trigger the headshot indicator animation. Resets if already active. */
  show(critical = false): void {
    this._isActive = true;
    this._age = 0;
    this._scale = 0;
    this._opacity = 1;
    this._critical = critical;
    this.applyVariantStyle();
    if (critical && typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hud-critical-shake'));
    }
    this.applyStyle();
  }

  /** Advance animation — call once per frame with delta-time in seconds. */
  update(dt: number): void {
    if (!this._isActive) return;

    this._age += dt;

    if (this._age >= TOTAL_DURATION) {
      this._isActive = false;
      this._opacity = 0;
      this._scale = 0;
      this.applyStyle();
      return;
    }

    // Phase 1: scale up 0 → 1.2 over first 0.15 s
    if (this._age < SCALE_UP_DURATION) {
      const t = this._age / SCALE_UP_DURATION;
      const overshoot = this._critical ? CRITICAL_OVERSHOOT_SCALE : OVERSHOOT_SCALE;
      this._scale = t * overshoot;
      this._opacity = 1;
    }
    // Phase 2: scale down 1.2 → 1.0 over next 0.15 s
    else if (this._age < SCALE_TOTAL) {
      const t = (this._age - SCALE_UP_DURATION) / SCALE_DOWN_DURATION;
      const overshoot = this._critical ? CRITICAL_OVERSHOOT_SCALE : OVERSHOOT_SCALE;
      const settle = this._critical ? CRITICAL_SETTLE_SCALE : SETTLE_SCALE;
      this._scale = overshoot - t * (overshoot - settle);
      this._opacity = 1;
    }
    // Phase 3: fade out over 1.0 s
    else {
      this._scale = this._critical ? CRITICAL_SETTLE_SCALE : SETTLE_SCALE;
      const fadeAge = this._age - SCALE_TOTAL;
      this._opacity = Math.max(0, 1 - fadeAge / FADE_DURATION);
    }

    this.applyStyle();
  }

  dispose(): void {
    this._isActive = false;
    this._age = 0;
    this._scale = 0;
    this._opacity = 0;
    this._critical = false;
    this.container.remove();
  }

  // ── Private ──

  private applyStyle(): void {
    this.el.style.opacity = String(this._opacity);
    this.el.style.transform = `translateX(-50%) scale(${this._scale})`;
    this.el.style.display = this._isActive ? '' : 'none';
  }

  private applyVariantStyle(): void {
    this.el.textContent = this._critical ? '\u2620 CRITICAL HIT' : '\u2620 HEADSHOT';
    this.el.style.color = this._critical ? '#fff36a' : '#ff4444';
    this.el.style.textShadow = this._critical
      ? '0 0 14px rgba(255,243,106,1), 0 0 28px rgba(255,68,68,0.9), 2px 2px 4px #000'
      : '0 0 8px rgba(255,68,68,0.8), 2px 2px 4px #000';
  }
}
